/**
 * CardioQ Patient App — root component.
 *
 * Routes are registered here; the actual screens are imported lazily so
 * the kiosk's first paint stays fast on the W/OHS landing.
 *
 * Each protected route is wrapped in <ProtectedRoute> (task 28) which
 * enforces JWT + PATIENT role; the Home/Score/OHS/History/Profile/
 * Methodology/Documents routes go through that wrapper.
 */

import { Suspense, lazy } from 'react';
import { Route, Routes } from 'react-router-dom';
import { PatientLayout } from './components/PatientLayout';
import { EmployerLayout } from './components/EmployerLayout';
import { AppLoader } from './components/AppLoader';
import { INSURANCE_ROLES } from './insurance/lib/persona';

// Lazy-load to keep the login bundle small.
// Landing (+ its Three.js bundle) and role-select are lazy so the WebGL
// payload never lands in the main app/login chunk.
const Landing = lazy(() => import('./components/hero/cardioq-hero'));
const RoleSelect = lazy(() => import('./pages/RoleSelect'));
// 60-second mobile sign-up / sign-in (founder prototype); health details come later.
const Start = lazy(() => import('./pages/Start'));
// Deferred health-data capture (Vitals / Cardiology / Pathology / Radiology).
const HealthSnapshot = lazy(() => import('./pages/HealthSnapshot'));
const Login = lazy(() => import('./pages/Login'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const Register = lazy(() => import('./pages/Register'));
const Terms = lazy(() => import('./pages/Terms'));
const Home = lazy(() => import('./pages/Home'));
const ScoreDetail = lazy(() => import('./pages/ScoreDetail'));
const OHSDetail = lazy(() => import('./pages/OHSDetail'));
const History = lazy(() => import('./pages/History'));
const Profile = lazy(() => import('./pages/Profile'));
const Methodology = lazy(() => import('./pages/Methodology'));
const Documents = lazy(() => import('./pages/Documents'));
const Compute = lazy(() => import('./pages/Compute'));
const EmployerDashboard = lazy(() => import('./pages/EmployerDashboard'));
const EmployerPlayground = lazy(() => import('./pages/EmployerPlayground'));
const HeartDemo = lazy(() => import('./pages/demo/HeartDemo'));
const HeartScoreDemo = lazy(() => import('./pages/HeartScoreDemo'));
const CorporateDemo = lazy(() => import('./pages/demo/CorporateDemo'));
// Insurer-facing value walkthrough — public, no auth, so it opens in a
// meeting without a login. Its own chunk (pulls the insurance design scope).
const InsurerExperience = lazy(() => import('./insurance/experience/InsurerExperience'));
const SharedScore = lazy(() => import('./pages/SharedScore'));
const NotFound = lazy(() => import('./pages/NotFound'));
// Insurance section — its own chunk (dashboards + scoped styles); the
// internal persona router lives in insurance/InsuranceApp.tsx.
const InsuranceApp = lazy(() => import('./insurance/InsuranceApp'));

export default function App() {
  return (
    <Suspense fallback={<AppLoader />}>
      <Routes>
        {/* The app opens straight on the founder prototype's sign-up splash
            (same flow at /start). The old marketing landing + role picker are
            kept, unlinked, at /welcome and /get-started — like /register. */}
        <Route path="/" element={<Start />} />
        <Route path="/welcome" element={<Landing />} />
        <Route path="/get-started" element={<RoleSelect />} />

        {/* Public */}
        <Route path="/login" element={<Login />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/start" element={<Start />} />
        <Route path="/register" element={<Register />} />
        {/* Terms & Conditions — public so it's readable from the signup
            checkbox link (and pre-login). */}
        <Route path="/terms" element={<Terms />} />

        {/* Public marketing demos (no auth) — re-implemented in our design
            language from the /dashboard/*-demo microsites. */}
        <Route path="/heart-demo" element={<HeartDemo />} />
        {/* Campaign video → sign-up. Public; the MP4 lives in /media/, not git. */}
        <Route path="/heart-score-demo" element={<HeartScoreDemo />} />
        <Route path="/corporate-demo" element={<CorporateDemo />} />
        <Route path="/insurer-experience" element={<InsurerExperience />} />

        {/* Public, tokenized clinician view of a patient's shared summary.
            No auth — the backend endpoint is API-key-exempt and 404s on an
            expired/revoked token. Must stay OUTSIDE any protected wrapper. */}
        <Route path="/shared/:token" element={<SharedScore />} />

        {/* Protected + layout-wrapped routes */}
        <Route path="/home" element={<PatientLayout><Home /></PatientLayout>} />
        <Route path="/health-snapshot" element={<PatientLayout><HealthSnapshot /></PatientLayout>} />
        <Route path="/score" element={<PatientLayout><ScoreDetail /></PatientLayout>} />
        <Route path="/ohs" element={<PatientLayout><OHSDetail /></PatientLayout>} />
        <Route path="/history" element={<PatientLayout><History /></PatientLayout>} />
        <Route path="/profile" element={<PatientLayout><Profile /></PatientLayout>} />
        <Route path="/methodology" element={<PatientLayout><Methodology /></PatientLayout>} />
        <Route path="/documents" element={<PatientLayout><Documents /></PatientLayout>} />

        {/* DEV: manual InputVector → live engine compute. Remove before
            production if Product decides patients shouldn't have direct
            what-if access. */}
        <Route path="/compute" element={<PatientLayout><Compute /></PatientLayout>} />

        {/* Employer CXO dashboard + playground — gated to the EMPLOYER role. */}
        <Route
          path="/employer"
          element={<EmployerLayout><EmployerDashboard /></EmployerLayout>}
        />
        <Route
          path="/employer/playground"
          element={<EmployerLayout><EmployerPlayground /></EmployerLayout>}
        />

        {/* Insurance section — one shell, persona-routed inside. The
            ProtectedRoute allow-list widens to every insurance role;
            InsuranceApp's Gate enforces per-persona access on top. */}
        <Route
          path="/insurance/*"
          element={
            <PatientLayout allowRoles={INSURANCE_ROLES} audienceLabel="insurance users">
              <InsuranceApp />
            </PatientLayout>
          }
        />

        {/* 404 */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}
